/**
 * The authoritative fleet health grade.
 *
 * Distinct from the client-side dashboard next to it, which derives quick
 * proxies from live state. This is the server's composite of pre-flight
 * readiness, return-home margin and separation — worst-signal-governs, so a
 * single aircraft that cannot get home pins the whole grade to critical and the
 * number to the red. The dashboard shows the texture; this shows the verdict.
 */

import { api, type ApiResult } from '@/api/client';

export type HealthGrade = 'healthy' | 'degraded' | 'critical' | 'unknown';

export interface HealthSignal {
  name: string;
  grade: HealthGrade;
  detail: string;
}

export interface AircraftHealth {
  droneId: string;
  grade: HealthGrade;
  score: number;
  summary: string;
  signals: HealthSignal[];
}

/**
 * One sensor's health across the aircraft that actually have it, decoded from
 * the SYS_STATUS masks the aircraft send rather than from whether a message
 * arrived. The server owns the decoding: the MAVLink sensor enum is a domain
 * concern and a second reading of it here would be a second thing to keep true.
 */
export interface SubsystemHealth {
  name: string;
  /** Reporting this sensor enabled and healthy. */
  healthy: number;
  /** Enabled and reporting unhealthy. The number that matters. */
  unhealthy: number;
  /** Fitted but switched off — configuration, not a fault. */
  notEnabled: number;
  /**
   * Healthy share of the aircraft that have it enabled, or null when none do.
   * Null rather than 100: "every aircraft that uses this is fine" and "no
   * aircraft uses this" are different sentences.
   */
  percent: number | null;
}

export interface FleetHealth {
  grade: HealthGrade;
  score: number;
  counts: Record<HealthGrade, number>;
  worstReason: string;
  aircraft: AircraftHealth[];
  /** Worst first. Absent on a server older than this field. */
  subsystems?: SubsystemHealth[];
  /**
   * How many aircraft sent a decodable SYS_STATUS, and how many there are. A
   * sensor bar drawn from two aircraft out of nine is not a fleet reading, and
   * the panel has no way to know that unless it is told.
   */
  subsystemsReporting?: number;
  subsystemsTotal?: number;
}

export function getFleetHealth(): Promise<ApiResult<FleetHealth>> {
  return api.get<FleetHealth>('/fleet-health', { logged: false });
}
